import React, { useRef, useEffect, memo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './PMCalibrationTracker.css';

// Sanitize image data to remove C2PA metadata and fix malformed data URLs
const sanitizeImageData = (data) => {
  if (!data || typeof data !== 'string') return data;
  
  // Remove HTML entities that may have been encoded
  let cleaned = data
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#39;/g, "'");
  
  // Remove array brackets if the data was incorrectly wrapped
  cleaned = cleaned.replace(/^\[/, '').replace(/\]$/, '');
  
  // Remove C2PA metadata - find the actual data URL start
  const dataUrlMatch = cleaned.match(/(data:image\/[^;]+;base64,[A-Za-z0-9+/=]+)/);
  if (dataUrlMatch) {
    return dataUrlMatch[1];
  }
  
  // Check for PDF data URL
  const pdfMatch = cleaned.match(/(data:application\/pdf;base64,[A-Za-z0-9+/=]+)/);
  if (pdfMatch) {
    return pdfMatch[1];
  }
  
  // If no match found but data starts with data:, return as-is
  if (cleaned.startsWith('data:')) {
    return cleaned;
  }
  
  // Return original if we can't clean it
  return data;
};

// Memoized page thumbnail component for better performance
const PageThumbnail = memo(({ page, index, type, onRemove }) => {
  // Sanitize the page data before rendering
  const sanitizedPage = sanitizeImageData(page);
  
  return (
    <div style={{ 
      position: 'relative', 
      aspectRatio: '3/4', 
      borderRadius: '0.375rem', 
      overflow: 'hidden', 
      border: '2px solid #e2e8f0',
      backgroundColor: '#f8fafc'
    }}>
      <img 
        src={sanitizedPage} 
        alt={`${type} Page ${index + 1}`} 
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        loading="lazy"
        onError={(e) => {
          console.error('Thumbnail failed to load:', sanitizedPage?.substring(0, 100));
          e.target.style.display = 'none';
        }}
      />
    {/* Gradient overlay */}
    <div style={{ 
      position: 'absolute', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      background: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, transparent 30%, transparent 70%, rgba(0,0,0,0.4) 100%)',
      pointerEvents: 'none'
    }} />
    
    {/* Page number - top left */}
    <span style={{ 
      position: 'absolute', 
      top: '0.25rem', 
      left: '0.25rem', 
      background: 'rgba(0,0,0,0.6)', 
      color: 'white', 
      padding: '0.125rem 0.375rem', 
      borderRadius: '0.25rem', 
      fontSize: '0.75rem',
      pointerEvents: 'none'
    }}>
      {index + 1}
    </span>
    
    {/* Delete button - top right, inside thumbnail */}
    <button 
      type="button"
      onClick={(e) => { 
        e.preventDefault();
        e.stopPropagation(); 
        onRemove(type, index); 
      }}
      style={{ 
        position: 'absolute', 
        top: '0.25rem', 
        right: '0.25rem', 
        background: 'rgba(239, 68, 68, 0.95)', 
        color: 'white', 
        border: 'none', 
        borderRadius: '50%', 
        width: '1.25rem', 
        height: '1.25rem', 
        cursor: 'pointer', 
        fontSize: '0.625rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 1,
        padding: 0,
        margin: 0,
        minWidth: '1.25rem',
        minHeight: '1.25rem'
      }}
    >
      ✕
    </button>
  </div>
  );
});

const ReportUploadModal = ({
  isOpen,
  onClose,
  asset,
  reportUploadForm,
  setReportUploadForm,
  onSave,
  onFileChange,
  onStartCamera,
  onRemovePage,
  onUpdateAssetDates
}) => {
  // Helper function to convert DD-Mon-YYYY to YYYY-MM-DD for date inputs
  const convertCustomToCalendarFormat = (customDate) => {
    // Handle non-string values (Date objects, null, undefined)
    if (customDate instanceof Date) {
      const yyyy = customDate.getFullYear();
      const mm = String(customDate.getMonth() + 1).padStart(2, '0');
      const dd = String(customDate.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }
    if (typeof customDate !== 'string' || !customDate || customDate.trim() === '' || customDate === 'NA' || customDate === 'NR') return '';
    
    try {
      // Parse DD-Mon-YYYY format
      const months = {
        'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
        'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
      };
      const parts = customDate.split('-');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = months[parts[1]];
        const year = parseInt(parts[2], 10);
        if (!isNaN(day) && month !== undefined && !isNaN(year)) {
          const date = new Date(year, month, day);
          const yyyy = date.getFullYear();
          const mm = String(date.getMonth() + 1).padStart(2, '0');
          const dd = String(date.getDate()).padStart(2, '0');
          return `${yyyy}-${mm}-${dd}`;
        }
      }
      // If already in YYYY-MM-DD format, return as-is
      if (/^\d{4}-\d{2}-\d{2}$/.test(customDate)) {
        return customDate;
      }
      return '';
    } catch (error) {
      console.warn('Error converting custom date to calendar format:', error);
      return '';
    }
  };
  // Ensure reportUploadForm has default values to prevent undefined errors
  const form = reportUploadForm || {
    pmReportFile: null,
    pmReportPreview: '',
    pmReportPages: [],
    calReportFile: null,
    calReportPreview: '',
    calReportPages: []
  };
  
  const pmFileInputRef = useRef(null);
  const calFileInputRef = useRef(null);
  const modalRef = useRef(null);
  const isOpenRef = useRef(isOpen);

  // Handle file selection - supports multiple files
  const handlePmFileSelect = (e) => {
    e.stopPropagation();
    if (e.target.files && e.target.files.length > 0) {
      // Handle multiple files
      Array.from(e.target.files).forEach((file, index) => {
        onFileChange('pm', file, index > 0); // Append for additional files
      });
    }
    // Reset input to allow selecting same files again
    if (pmFileInputRef.current) {
      pmFileInputRef.current.value = '';
    }
  };

  const handleCalFileSelect = (e) => {
    e.stopPropagation();
    if (e.target.files && e.target.files.length > 0) {
      // Handle multiple files
      Array.from(e.target.files).forEach((file, index) => {
        onFileChange('cal', file, index > 0); // Append for additional files
      });
    }
    // Reset input to allow selecting same files again
    if (calFileInputRef.current) {
      calFileInputRef.current.value = '';
    }
  };

  // Prevent closing when clicking inside modal
  const handleModalClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  // Prevent any mouse events from closing modal
  const handleMouseEvent = (e) => {
    e.stopPropagation();
  };

  // Handle file button click
  const handlePmFileButtonClick = (e) => {
    e.stopPropagation();
    if (pmFileInputRef.current) {
      pmFileInputRef.current.click();
    }
  };

  // Handle cal file button click
  const handleCalFileButtonClick = (e) => {
    e.stopPropagation();
    if (calFileInputRef.current) {
      calFileInputRef.current.click();
    }
  };

  // Handle camera button click
  const handlePmCameraClick = (e) => {
    e.stopPropagation();
    onStartCamera('pm');
  };

  // Handle cal camera button click
  const handleCalCameraClick = (e) => {
    e.stopPropagation();
    onStartCamera('cal');
  };

  // Track isOpen changes for optimization
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  // Close on escape key - optimized
  useEffect(() => {
    if (!isOpen) return;
    
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpenRef.current) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const modalContent = (
    <div 
      className="modal-overlay" 
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 99999,
        padding: '1rem'
      }}
    >
      <div 
        ref={modalRef}
        className="modal-content"
        style={{
          background: 'white',
          borderRadius: '0.5rem',
          width: '90%',
          maxWidth: '600px',
          maxHeight: '90vh',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1rem 1.5rem',
          borderBottom: '1px solid #e2e8f0'
        }}>
          <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.25rem' }}>
            📄 Upload PM/Calibration Reports
          </h3>
          <button 
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#64748b',
              padding: '0.25rem',
              lineHeight: 1
            }}
          >
            ×
          </button>
        </div>
        
        <div className="modal-body" style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
          {asset && (
            <div style={{
              backgroundColor: '#f1f5f9',
              padding: '1rem',
              borderRadius: '0.375rem',
              marginBottom: '1.5rem'
            }}>
              <p style={{ margin: '0.25rem 0' }}><strong>Equipment:</strong> {asset.equipmentName}</p>
              <p style={{ margin: '0.25rem 0' }}><strong>Asset ID:</strong> {asset.assetId}</p>
              <p style={{ margin: '0.25rem 0' }}><strong>Serial No:</strong> {asset.serialNo}</p>
              
              {/* PM Date Editing */}
              <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#eff6ff', borderRadius: '0.375rem', border: '1px solid #bfdbfe' }}>
                <h4 style={{ margin: '0 0 0.5rem 0', fontWeight: '600', color: '#1e40af', fontSize: '1rem' }}>🔧 PM Dates</h4>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <strong>Service Date:</strong>
                    <select
                      value={asset.pmDone === 'NA' || asset.pmDone === 'NR' ? asset.pmDone : 'DATE'}
                      onChange={(e) => {
                        const value = e.target.value;
                        const newDoneDate = value === 'DATE' ? '' : value;
                        const newDueDate = value === 'DATE' ? '' : value;
                        onUpdateAssetDates(asset.id, 'pmDone', newDoneDate);
                        onUpdateAssetDates(asset.id, 'pmDue', newDueDate);
                      }}
                      style={{ padding: '0.25rem 0.5rem', border: '1px solid #cbd5e1', borderRadius: '0.25rem', fontSize: '0.875rem' }}
                    >
                      <option value="DATE">Select Date</option>
                      <option value="NA">NA</option>
                      <option value="NR">NR</option>
                    </select>
                    {(asset.pmDone !== 'NA' && asset.pmDone !== 'NR') && (
                      <input
                        type="date"
                        value={convertCustomToCalendarFormat(asset.pmDone) || ''}
                        onChange={(e) => {
                          const newDate = e.target.value;
                          // Calculate due date based on selected auto-calculation period
                          const autoCalcPeriod = asset.pmAutoCalcPeriod || 6; // Default to 6 months
                          let dueDate = '';
                          if (newDate) {
                            const doneDateObj = new Date(newDate);
                            if (!isNaN(doneDateObj.getTime())) {
                              const dueDateObj = new Date(doneDateObj);
                              dueDateObj.setMonth(dueDateObj.getMonth() + autoCalcPeriod);
                              dueDateObj.setDate(dueDateObj.getDate() - 1);
                              dueDate = dueDateObj.toISOString().split('T')[0];
                            }
                          }
                          onUpdateAssetDates(asset.id, 'pmDone', newDate);
                          onUpdateAssetDates(asset.id, 'pmDue', dueDate);
                        }}
                        style={{ padding: '0.25rem 0.5rem', border: '1px solid #cbd5e1', borderRadius: '0.25rem', fontSize: '0.875rem' }}
                      />
                    )}
                  </span>
                  <span style={{ color: '#94a3b8' }}>|</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <strong>Due Date:</strong>
                    <select
                      value={asset.pmDue === 'NA' || asset.pmDue === 'NR' ? asset.pmDue : 'DATE'}
                      onChange={(e) => {
                        const value = e.target.value;
                        const newDueDate = value === 'DATE' ? '' : value;
                        onUpdateAssetDates(asset.id, 'pmDue', newDueDate);
                      }}
                      style={{ padding: '0.25rem 0.5rem', border: '1px solid #cbd5e1', borderRadius: '0.25rem', fontSize: '0.875rem' }}
                    >
                      <option value="DATE">Select Date</option>
                      <option value="NA">NA</option>
                      <option value="NR">NR</option>
                    </select>
                    {(asset.pmDue !== 'NA' && asset.pmDue !== 'NR') && (
                      <input
                        type="date"
                        value={convertCustomToCalendarFormat(asset.pmDue) || ''}
                        onChange={(e) => onUpdateAssetDates(asset.id, 'pmDue', e.target.value)}
                        style={{ padding: '0.25rem 0.5rem', border: '1px solid #cbd5e1', borderRadius: '0.25rem', fontSize: '0.875rem' }}
                      />
                    )}
                    <select
                      value={asset.pmAutoCalcPeriod || 6}
                      onChange={(e) => {
                        const months = parseInt(e.target.value, 10);
                        onUpdateAssetDates(asset.id, 'pmAutoCalcPeriod', months);
                        // Recalculate due date if service date exists
                        if (asset.pmDone && asset.pmDone !== 'NA' && asset.pmDone !== 'NR') {
                          const doneDate = convertCustomToCalendarFormat(asset.pmDone);
                          if (doneDate) {
                            const doneDateObj = new Date(doneDate);
                            if (!isNaN(doneDateObj.getTime())) {
                              const dueDateObj = new Date(doneDateObj);
                              dueDateObj.setMonth(dueDateObj.getMonth() + months);
                              dueDateObj.setDate(dueDateObj.getDate() - 1);
                              const newDueDate = dueDateObj.toISOString().split('T')[0];
                              onUpdateAssetDates(asset.id, 'pmDue', newDueDate);
                            }
                          }
                        }
                      }}
                      style={{ padding: '0.25rem 0.5rem', border: '1px solid #cbd5e1', borderRadius: '0.25rem', fontSize: '0.75rem', color: '#64748b' }}
                    >
                      <option value={3}>Auto: +3 months - 1 day</option>
                      <option value={6}>Auto: +6 months - 1 day</option>
                      <option value={12}>Auto: +12 months - 1 day</option>
                    </select>
                  </span>
                </div>
              </div>
              
              {/* Calibration Date Editing */}
              <h4 style={{ marginTop: '0.75rem', padding: '0.75rem', backgroundColor: '#f0fdf4', borderRadius: '0.375rem', border: '1px solid #bbf7d0', fontWeight: '600', color: '#166534', fontSize: '1rem' }}>
                ⚖️ Calibration Dates
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <strong>Service Date:</strong>
                    <select
                      value={asset.calDone === 'NA' || asset.calDone === 'NR' ? asset.calDone : 'DATE'}
                      onChange={(e) => {
                        const value = e.target.value;
                        const newDoneDate = value === 'DATE' ? '' : value;
                        const newDueDate = value === 'DATE' ? '' : value;
                        onUpdateAssetDates(asset.id, 'calDone', newDoneDate);
                        onUpdateAssetDates(asset.id, 'calDue', newDueDate);
                      }}
                      style={{ padding: '0.25rem 0.5rem', border: '1px solid #cbd5e1', borderRadius: '0.25rem', fontSize: '0.875rem' }}
                    >
                      <option value="DATE">Select Date</option>
                      <option value="NA">NA</option>
                      <option value="NR">NR</option>
                    </select>
                    {(asset.calDone !== 'NA' && asset.calDone !== 'NR') && (
                      <input
                        type="date"
                        value={convertCustomToCalendarFormat(asset.calDone) || ''}
                        onChange={(e) => {
                          const newDate = e.target.value;
                          // Calculate due date based on selected auto-calculation period
                          const autoCalcPeriod = asset.calAutoCalcPeriod || 12; // Default to 12 months
                          let dueDate = '';
                          if (newDate) {
                            const doneDateObj = new Date(newDate);
                            if (!isNaN(doneDateObj.getTime())) {
                              const dueDateObj = new Date(doneDateObj);
                              dueDateObj.setMonth(dueDateObj.getMonth() + autoCalcPeriod);
                              dueDateObj.setDate(dueDateObj.getDate() - 1);
                              dueDate = dueDateObj.toISOString().split('T')[0];
                            }
                          }
                          onUpdateAssetDates(asset.id, 'calDone', newDate);
                          onUpdateAssetDates(asset.id, 'calDue', dueDate);
                        }}
                        style={{ padding: '0.25rem 0.5rem', border: '1px solid #cbd5e1', borderRadius: '0.25rem', fontSize: '0.875rem' }}
                      />
                    )}
                  </span>
                  <span style={{ color: '#94a3b8' }}>|</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <strong>Due Date:</strong>
                    <select
                      value={asset.calDue === 'NA' || asset.calDue === 'NR' ? asset.calDue : 'DATE'}
                      onChange={(e) => {
                        const value = e.target.value;
                        const newDueDate = value === 'DATE' ? '' : value;
                        onUpdateAssetDates(asset.id, 'calDue', newDueDate);
                      }}
                      style={{ padding: '0.25rem 0.5rem', border: '1px solid #cbd5e1', borderRadius: '0.25rem', fontSize: '0.875rem' }}
                    >
                      <option value="DATE">Select Date</option>
                      <option value="NA">NA</option>
                      <option value="NR">NR</option>
                    </select>
                    {(asset.calDue !== 'NA' && asset.calDue !== 'NR') && (
                      <input
                        type="date"
                        value={convertCustomToCalendarFormat(asset.calDue) || ''}
                        onChange={(e) => onUpdateAssetDates(asset.id, 'calDue', e.target.value)}
                        style={{ padding: '0.25rem 0.5rem', border: '1px solid #cbd5e1', borderRadius: '0.25rem', fontSize: '0.875rem' }}
                      />
                    )}
                    <select
                      value={asset.calAutoCalcPeriod || 12}
                      onChange={(e) => {
                        const months = parseInt(e.target.value, 10);
                        onUpdateAssetDates(asset.id, 'calAutoCalcPeriod', months);
                        // Recalculate due date if service date exists
                        if (asset.calDone && asset.calDone !== 'NA' && asset.calDone !== 'NR') {
                          const doneDate = convertCustomToCalendarFormat(asset.calDone);
                          if (doneDate) {
                            const doneDateObj = new Date(doneDate);
                            if (!isNaN(doneDateObj.getTime())) {
                              const dueDateObj = new Date(doneDateObj);
                              dueDateObj.setMonth(dueDateObj.getMonth() + months);
                              dueDateObj.setDate(dueDateObj.getDate() - 1);
                              const newDueDate = dueDateObj.toISOString().split('T')[0];
                              onUpdateAssetDates(asset.id, 'calDue', newDueDate);
                            }
                          }
                        }
                      }}
                      style={{ padding: '0.25rem 0.5rem', border: '1px solid #cbd5e1', borderRadius: '0.25rem', fontSize: '0.75rem', color: '#64748b' }}
                    >
                      <option value={3}>Auto: +3 months - 1 day</option>
                      <option value={6}>Auto: +6 months - 1 day</option>
                      <option value={12}>Auto: +12 months - 1 day</option>
                    </select>
                  </span>
                </div>
              </h4>
            </div>
          )}

          {/* PM Report Section */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ marginBottom: '0.75rem' }}>
              🔧 PM Report {form.pmReportPages.length > 0 && `(${form.pmReportPages.length} pages)`}
            </h4>
            <div style={{ border: '2px dashed #e2e8f0', padding: '1rem', borderRadius: '0.5rem' }}>
              <input
                type="file"
                ref={pmFileInputRef}
                accept=".jpg,.jpeg,.png,.pdf"
                multiple
                onChange={handlePmFileSelect}
                style={{ display: 'none' }}
              />
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                <button 
                  type="button"
                  onClick={handlePmFileButtonClick}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                >
                  📁 Choose Files
                </button>
                <button 
                  type="button"
                  onClick={handlePmCameraClick}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#64748b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                >
                  📷 Take Photo
                </button>
              </div>
              <p style={{ fontSize: '0.875rem', color: '#64748b', margin: 0 }}>
                Supported: JPG, PNG, PDF (Max 5MB each). You can select multiple files.
              </p>
              
              {form.pmReportFile && (
                <div style={{ marginTop: '0.5rem' }}>
                  <p style={{ color: '#10b981', marginBottom: '0.5rem' }}>
                    Selected: {form.pmReportFile.name}
                  </p>
                  {/* Preview for image files */}
                  {/* Preview for image files */}
                  {sanitizeImageData(form.pmReportPreview)?.startsWith('data:image') && (
                    <div style={{ 
                      maxWidth: '200px', 
                      border: '1px solid #e2e8f0', 
                      borderRadius: '0.375rem', 
                      overflow: 'hidden',
                      marginTop: '0.5rem'
                    }}>
                      <img 
                        src={sanitizeImageData(form.pmReportPreview)} 
                        alt="PM Report Preview" 
                        style={{ width: '100%', height: 'auto', display: 'block' }}
                        onError={(e) => {
                          console.error('PM Preview failed to load');
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  {/* Preview for PDF files */}
                  {form.pmReportPreview?.startsWith('data:application/pdf') && (
                    <div style={{ 
                      padding: '1rem', 
                      backgroundColor: '#f1f5f9', 
                      borderRadius: '0.375rem',
                      marginTop: '0.5rem',
                      textAlign: 'center'
                    }}>
                      <span style={{ fontSize: '2rem' }}>📄</span>
                      <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: '#64748b' }}>PDF File</p>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                        <a
                          href={form?.pmReportPreview || '#'}
                          target="_blank"
                          download={form?.pmReportFile?.name || 'PM_Report.pdf'}
                          onClick={(e) => {
                            if (!form?.pmReportPreview || !form.pmReportPreview.startsWith('data:')) {
                              e.preventDefault();
                              alert('No PDF data available. Please upload a PDF file first.');
                            }
                          }}
                          style={{
                            padding: '0.375rem 0.75rem',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.25rem',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            textDecoration: 'none',
                            display: 'inline-block'
                          }}
                        >
                          👁️ View / Download PDF
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {form.pmReportPages.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <p style={{ fontWeight: 600, marginBottom: '0.75rem' }}>📄 Captured Pages ({form.pmReportPages.length}):</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '0.5rem' }}>
                    {form.pmReportPages.map((page, index) => (
                      <PageThumbnail 
                        key={`pm-${index}`}
                        page={page}
                        index={index}
                        type="PM"
                        onRemove={onRemovePage}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Calibration Report Section */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ marginBottom: '0.75rem' }}>
              ⚖️ Calibration Report {form.calReportPages.length > 0 && `(${form.calReportPages.length} pages)`}
            </h4>
            <div style={{ border: '2px dashed #e2e8f0', padding: '1rem', borderRadius: '0.5rem' }}>
              <input
                type="file"
                ref={calFileInputRef}
                accept=".jpg,.jpeg,.png,.pdf"
                multiple
                onChange={handleCalFileSelect}
                style={{ display: 'none' }}
              />
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                <button 
                  type="button"
                  onClick={handleCalFileButtonClick}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                >
                  📁 Choose Files
                </button>
                <button 
                  type="button"
                  onClick={handleCalCameraClick}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#64748b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                >
                  📷 Take Photo
                </button>
              </div>
              <p style={{ fontSize: '0.875rem', color: '#64748b', margin: 0 }}>
                Supported: JPG, PNG, PDF (Max 5MB each). You can select multiple files.
              </p>
              
              {form.calReportFile && (
                <div style={{ marginTop: '0.5rem' }}>
                  <p style={{ color: '#10b981', marginBottom: '0.5rem' }}>
                    Selected: {form.calReportFile.name}
                  </p>
                  {/* Preview for image files */}
                  {sanitizeImageData(form.calReportPreview)?.startsWith('data:image') && (
                    <div style={{ 
                      maxWidth: '200px', 
                      border: '1px solid #e2e8f0', 
                      borderRadius: '0.375rem', 
                      overflow: 'hidden',
                      marginTop: '0.5rem'
                    }}>
                      <img 
                        src={sanitizeImageData(form.calReportPreview)} 
                        alt="Calibration Report Preview" 
                        style={{ width: '100%', height: 'auto', display: 'block' }}
                        onError={(e) => {
                          console.error('Cal Preview failed to load');
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  {/* Preview for PDF files */}
                  {form.calReportPreview?.startsWith('data:application/pdf') && (
                    <div style={{ 
                      padding: '1rem', 
                      backgroundColor: '#f1f5f9', 
                      borderRadius: '0.375rem',
                      marginTop: '0.5rem',
                      textAlign: 'center'
                    }}>
                      <span style={{ fontSize: '2rem' }}>📄</span>
                      <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: '#64748b' }}>PDF File</p>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                        <a
                          href={form?.calReportPreview || '#'}
                          target="_blank"
                          download={form?.calReportFile?.name || 'Calibration_Report.pdf'}
                          onClick={(e) => {
                            if (!form?.calReportPreview || !form.calReportPreview.startsWith('data:')) {
                              e.preventDefault();
                              alert('No PDF data available. Please upload a PDF file first.');
                            }
                          }}
                          style={{
                            padding: '0.375rem 0.75rem',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.25rem',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            textDecoration: 'none',
                            display: 'inline-block'
                          }}
                        >
                          👁️ View / Download PDF
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {form.calReportPages.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <p style={{ fontWeight: 600, marginBottom: '0.75rem' }}>📄 Captured Pages ({form.calReportPages.length}):</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '0.5rem' }}>
                    {form.calReportPages.map((page, index) => (
                      <PageThumbnail 
                        key={`cal-${index}`}
                        page={page}
                        index={index}
                        type="Cal"
                        onRemove={onRemovePage}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div style={{
          display: 'flex',
          gap: '0.75rem',
          justifyContent: 'flex-end',
          padding: '1rem 1.5rem',
          borderTop: '1px solid #e2e8f0',
          flexShrink: 0
        }}>
          <button 
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onSave(); }}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
          >
            💾 Save Reports
          </button>
          <button 
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#64748b',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
          >
            ❌ Cancel
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

// Memoize the modal to prevent unnecessary re-renders
export default memo(ReportUploadModal, (prevProps, nextProps) => {
  // Only re-render if isOpen changes or if reportUploadForm reference changes significantly
  return (
    prevProps.isOpen === nextProps.isOpen &&
    prevProps.asset?.assetId === nextProps.asset?.assetId &&
    prevProps.reportUploadForm === nextProps.reportUploadForm
  );
});
